import axios from "axios";

import { getLicensesByLocation, Licenses } from "./licensesService";
import {
  getCompanyLocationTabUser,
  TabUserResponse,
} from "./locationUsersService";

export interface CompanyResponse {
  userId: string;
  roleId: number;
  wageTypeId: number;
  fee: number;
  fullName: string;
  email: string;
  mobilePhones: string;
  address: string;
  notes: string;
  gender: number;
  password: string;
  status: boolean;
  refreshToken: string;
  userName: string;
  citizenId: number;
  isTablet: boolean;
  companyId: string;
  avatarPhotoPath: string;
}

export interface LocationDto {
  code?: number;
  name: string;
  email: string;
  phone: string;
  avatarPhoto?: string;
  countryId?: number;
  cityId?: number;
  address: string;
  postCode?: number;
  bankName?: number;
  companyId: string;
  tabletUserName: string;
  tabletPassword: string;
  licenses: LocationLicensesDto[];
}

export interface LocationLicensesDto {
  companyId: string;
  moduleId: string;
  invoiceDate: number;
}

export interface UpdateLocationDto {
  id: string;
  code: number;
  name: string;
  email: string;
  phone: string;
  avatarPhoto?: string;
  countryId?: number;
  cityId?: number;
  address: string;
  postCode?: number;
  bankName?: string;
  bankBrachCode?: string;
  accountNumber?: string;
  accountClosingDay?: number;
  companyId: string;
}

export async function getUserCompany(userId: string) {
  return axios
    .get<CompanyResponse>(`/Users/getusercompanies/${userId}`)
    .then((res) => res.data);
}

export interface CompanyLocation {
  licenses?: Licenses;
  tabUser?: TabUserResponse;
  id: string;
  code: number;
  name: string;
  email: string;
  phone: string;
  avatarPhoto: string;
  countryId: number;
  cityId: number;
  address: string;
  postCode: number;
  bankName: string;
  bankBranchCode: string;
  accountNumber: string;
  isDeleted: boolean;
}

export enum LicenseTypes {
  shift = "Shift",
  food = "Food",
}

export async function getLocationsByCompany(companyId: string) {
  return axios
    .get<CompanyLocation[]>("/CompanyLocations/getlocationsfromcompany", {
      params: { companyId },
    })
    .then((res) => res.data);
}

export async function getActiveLocations(companyId: string) {
  return axios
    .get<CompanyLocation[]>("/CompanyLocations/getlocationsfromcompany", {
      params: { companyId },
    })
    .then((res) =>
      res.data
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((l) => !l.isDeleted),
    );
}

export interface LocationUser {
  companyLocationId: string;
  userId: string;
  userName: string;
  firstName: string;
  lastName: string;
  wage: number;
  wageType: number;
  userRole: number;
  userPhoto?: string;
  isDeleted?: boolean;
  userLocationPin?: string;
}

export async function getLocationUsers(locationId: string) {
  return axios
    .get<LocationUser[]>(`/CompanyLocationUsers/getlocationusersbyid`, {
      params: { id: locationId },
    })
    .then((res) => res.data);
}

function LicenseModuleIdTextPairs(LocationLicenses: Licenses): Licenses {
  const LicenseModuleIdTextPairs = [
    { moduleName: "FoodManagement", text: "Food", badgeType: "warning" },
    { moduleName: "ShiftManagement", text: "Shift", badgeType: "primary" },
    { moduleName: "TaskManagement", text: "Task", badgeType: "success" },
    {
      moduleName: "ShitManagementPro",
      text: "Shift Pro",
      badgeType: "danger",
    },
  ];

  // bind text to license module name
  LocationLicenses.data.map((license) => {
    const module = LicenseModuleIdTextPairs.find(
      (module) => module.moduleName === license.moduleName,
    );
    license.moduleName = module?.text || license.moduleName;
    license.badgeType = module?.badgeType || "primary";
    return license;
  });

  return LocationLicenses;
}

export async function getLicenses(locations: CompanyLocation[]) {
  return await Promise.all([
    Promise.all(
      locations.map((location) => getLicensesByLocation(location.id)),
    ).then((licenses) => licenses.map(LicenseModuleIdTextPairs)),
  ]);
}

export async function getLicensesAndTabUsers(
  locations: CompanyLocation[],
  companyId: string,
) {
  return (await Promise.all([
    Promise.all(
      locations.map((location) => getLicensesByLocation(location.id)),
    ).then((licenses) => licenses.map(LicenseModuleIdTextPairs)),
    Promise.all(
      locations.map((location) =>
        getCompanyLocationTabUser(location.id, companyId),
      ),
    ),
  ])) as [Licenses[], TabUserResponse[]];
}

export async function createLocation(value: Partial<LocationDto>) {
  return axios
    .post<LocationDto>(`/CompanyLocations/addlocation`, value)
    .then((res) => res.data);
}

export async function updateLocation(value: Partial<UpdateLocationDto>) {
  return axios
    .put<UpdateLocationDto>(`/CompanyLocations/updatelocation`, value)
    .then((res) => res.data);
}

export async function getLocationById(locationId: string) {
  return axios
    .get<UpdateLocationDto>(`/CompanyLocations/getlocationbyid/${locationId}`)
    .then((res) => res.data);
}

export interface updateLocation {
  locationId: string;
  status: boolean;
}
export async function updateLocationStatus(
  updateLocationData: Partial<updateLocation>,
) {
  return axios
    .put<string>(`/CompanyLocations/updatelocationstatus`, {
      id: updateLocationData.locationId,
      status: updateLocationData.status,
    })
    .then((res) => res.data);
}

export async function updateCompanyLocationSettings(
  updateLocationSettingsData: {
    companyId: string;
    companyLocationId: string;
    moduleId: string;
    invoiceDate: number;
  }[],
) {
  return axios
    .put<string>(`/CompanyLocations/updatecompanylocationsettings`, {
      licenses: [...updateLocationSettingsData],
    })
    .then((res) => res.data);
}

// interface CompanyLocationUser {
//   companyId: string;
//   companyLocationId: string;
//   userId: string;
//   userRole: number;
//   wage: number;
//   wageType: number;
// }

// export function getUserLocationInfo(locationId: string) {
//   return axios
//     .get<CompanyLocationUser>(
//       `/CompanyLocationUsers/getcompanylocationuserbyid`,
//       {
//         params: { id: locationId },
//       }
//     )
//     .then((res) => res.data);
// }
